import {type FC, useEffect} from 'react';
import {getPlaylistItems, getPlaylists} from '../../apis/callYoutubeApi.ts';
import {ContentWrapper} from './types/Replay.styled.ts';
import type {PlaylistInfo} from './types';
import {FocusContext, setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import PlayList from './component/PlayList.tsx';
import useReplayStore from '../../store/useReplayStore.ts';
import ReplayPlayer from './component/ReplayPlayer.tsx';
import useGlobalStore from '../../store/useGlobalStore.ts';

const ReplayPage: FC = () => {
    const {showPlayer, playListMap, setPlayListMap} = useReplayStore();

    const fetchData = async () => {
        const res = await getPlaylists();
        if (res?.data?.items) {
            const playListData = res.data.items;
            const mkPlayListMap = new Map<string, Array<PlaylistInfo>>();
            const playlistsWithIndex = playListData.map((playlist: PlaylistInfo, index: number) => ({
                playlist,
                index,
            }));
            await Promise.all(
                playlistsWithIndex.map(async ({playlist, index}: { playlist: PlaylistInfo; index: number }) => {
                    try {
                        const data = await getPlaylistItems(playlist.id);
                        const items = data?.data?.items ?? [];
                        mkPlayListMap.set(`${index}::${playlist.snippet.title}`, items);
                    } catch {
                        // 404 등 에러 발생 시 해당 플레이리스트 제외
                    }
                })
            );
            setPlayListMap(mkPlayListMap);
        }
    };

    useEffect(() => {
        fetchData();
        setFocus('replay');
    }, []);

    const {
        ref: replayPageRef,
        focusKey: replayPageFocusKey,
        focusSelf: focusReplayPage,
    } = useFocusable({
        focusKey: 'replay-page',
        preferredChildFocusKey: 'section-0',
        saveLastFocusedChild: true,
        trackChildren: true,
        isFocusBoundary: true,
    });

    const {ref: replayPlayerRef, focusKey: replayPlayerFocusKey} = useFocusable({
        focusKey: 'replay-player',
        preferredChildFocusKey: 'player-control-close',
        saveLastFocusedChild: true,
        trackChildren: true,
        isFocusBoundary: true,
    });

    const {stack} = useGlobalStore();
    useEffect(() => {
        if (stack.length > 0) setFocus(`${stack[stack.length - 1].id}_CLOSE_BUTTON`);
    }, [playListMap, focusReplayPage]);

    return (
        <>
            <FocusContext.Provider value={replayPageFocusKey}>
                <ContentWrapper ref={replayPageRef}>
                    {Array.from(playListMap.entries())
                        .sort(([keyA], [keyB]) => {
                            const indexA = parseInt(keyA.split('::')[0]);
                            const indexB = parseInt(keyB.split('::')[0]);
                            return indexA - indexB;
                        })
                        .map(([videoKey, videoList], sectionIndex) => (
                            <PlayList
                                key={videoKey}
                                title={videoKey.split('::')[1]}
                                videoList={videoList}
                                sectionIndex={sectionIndex}
                            />
                        ))}
                </ContentWrapper>
            </FocusContext.Provider>
            <FocusContext.Provider value={replayPlayerFocusKey}>
                {showPlayer && (
                    <div ref={replayPlayerRef}>
                        <ReplayPlayer/>
                    </div>
                )}
            </FocusContext.Provider>
        </>
    );
};

export default ReplayPage;
